import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { loadNikkeCatalog } from '../../nikke-catalog/api/nikkeCatalogApi.js';
import { buildPersonalNikkeLookup } from '../domain/personalNikke.js';

const DEFAULT_NIKKE_DATA_API_URL = 'https://dororaid-data.kukla1255.workers.dev';
const NIKKE_DATA_API_URL = String(
  import.meta.env?.VITE_DORORAID_DATA_API_URL || DEFAULT_NIKKE_DATA_API_URL,
).trim().replace(/\/+$/, '');
const PERSONAL_NIKKE_D1_TIMEOUT_MS = 10000;
const PERSONAL_NIKKE_SYNC_TIMEOUT_MS = 120000;

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('로그인 서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

async function currentAccessToken() {
  const { data, error } = await requireClient().auth.getSession();
  if (error) throw new Error(`로그인 세션을 확인하지 못했습니다: ${error.message}`);
  const accessToken = String(data?.session?.access_token || '').trim();
  if (!accessToken) throw new Error('로그인 세션이 만료되었습니다. 다시 로그인해주세요.');
  return accessToken;
}

function normalizeProfile(row) {
  if (!row) return null;
  return {
    nickname: String(row.nickname || '').trim(),
    playerLevel: Number(row.player_level) || 0,
    teamCombat: Number(row.team_combat) || 0,
    ownedCount: Number(row.owned_count) || 0,
    costumeCount: Number(row.costume_count) || 0,
    profileTeam: Array.isArray(row.profile_team) ? row.profile_team : [],
    syncedAt: String(row.synced_at || '').trim(),
  };
}

function normalizeD1Character(row = {}) {
  let equipment = row.equipment;
  if (typeof equipment === 'string') {
    try { equipment = JSON.parse(equipment); } catch { equipment = []; }
  }
  return {
    ...row,
    owned: row.owned !== false && Number(row.owned) !== 0,
    equipment: Array.isArray(equipment) ? equipment : [],
  };
}

async function requestPersonalNikke(path, { method = 'GET', timeoutMs = PERSONAL_NIKKE_D1_TIMEOUT_MS } = {}) {
  if (!NIKKE_DATA_API_URL) throw new Error('Cloudflare 개인 니케 API 주소가 설정되지 않았습니다.');
  const accessToken = await currentAccessToken();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${NIKKE_DATA_API_URL}${path}`, {
      method,
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${accessToken}`,
        ...(method === 'POST' ? { 'content-type': 'application/json' } : {}),
      },
      ...(method === 'POST' ? { body: '{}' } : {}),
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(payload?.message || payload?.error || `Cloudflare 개인 니케 응답 오류 (HTTP ${response.status})`);
      error.code = payload?.code || payload?.error || `http_${response.status}`;
      error.retryAfterSeconds = Number(payload?.retryAfterSeconds || 0);
      throw error;
    }
    return payload;
  } catch (error) {
    if (error?.name === 'AbortError') throw new Error('Cloudflare 개인 니케 응답 시간이 초과되었습니다.');
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function loadPersonalNikkeData() {
  const catalogPromise = loadNikkeCatalog();
  const payload = await requestPersonalNikke('/v1/personal-nikke');
  if (!Array.isArray(payload?.characters)) throw new Error('Cloudflare 개인 니케 응답 형식이 올바르지 않습니다.');

  const catalogResult = await catalogPromise;
  const lookup = buildPersonalNikkeLookup(payload.characters.map(normalizeD1Character), catalogResult.lookup);
  return {
    profile: normalizeProfile(payload.profile),
    characters: lookup.items,
    lookup,
    catalog: catalogResult.lookup,
    source: String(payload.source || 'cloudflare-d1'),
    warnings: [
      ...(Array.isArray(payload.warnings) ? payload.warnings.filter(Boolean) : []),
      ...(catalogResult.warnings || []),
    ],
  };
}

// Another union member's snapshot, read from the same Cloudflare D1 store as the viewer's own. The
// worker authorises the read through get_union_member_detail (leader/deputy of the same union) and
// resolves the member's linked account there, so this carries no permission logic of its own.
export async function loadMemberPersonalNikkeRows(memberOpenid) {
  const openid = String(memberOpenid || '').trim();
  if (!openid) throw new TypeError('memberOpenid is required');
  const query = new URLSearchParams({ openid });
  const payload = await requestPersonalNikke(`/v1/personal-nikke/member?${query}`);
  if (!Array.isArray(payload?.characters)) throw new Error('멤버 개인 니케 응답 형식이 올바르지 않습니다.');
  return {
    profile: normalizeProfile(payload.profile),
    characters: payload.characters.map(normalizeD1Character).filter((row) => row.owned),
    syncedAt: String(payload?.sync?.syncedAt || '').trim(),
  };
}

export async function syncPersonalNikkeData() {
  const payload = await requestPersonalNikke('/v1/personal-nikke/sync', {
    method: 'POST',
    timeoutMs: PERSONAL_NIKKE_SYNC_TIMEOUT_MS,
  });
  if (!payload?.ok) throw new Error(payload?.message || payload?.code || 'BlablaLink 니케 동기화에 실패했습니다.');
  return payload;
}
